import { useSession, useTheme } from '@zhiyue/core';
import { getStoredBrandLocale, syncBrandDocument } from 'config/brand';
import React from 'react';

type SyncUser = {
  id?: unknown;
  uuid?: unknown;
  username?: unknown;
  tenantUuid?: unknown;
} | null;

type Props = {
  /** 当前 initialState.settings.navTheme，用于发现异步初始态覆盖后重新对齐。 */
  currentNavTheme?: string;
  currentTitle?: string | false;
  currentUser?: SyncUser;
  setInitialState?: (updater: (prev: any) => any) => void;
};

const getUserSyncKey = (user?: SyncUser) =>
  user
    ? [user.id, user.uuid, user.username, user.tenantUuid]
        .filter(Boolean)
        .join('|')
    : '';

/**
 * ProLayout 的 navTheme / title 与头像数据来自 initialState.settings，
 * 而主题、语言和会话由 SessionProvider 维护。
 *
 * umi 的 rootContainer 第二个参数只有 { routes, plugin, history }
 * （见 https://umijs.org/docs/api/runtime-config#rootcontainer），
 * 在 rootContainer 里拿不到可用的 setInitialState，同步会静默失效，
 * 导致 ProLayout 一直停在初始的 realDark：亮色主题下折叠按钮、折叠弹层、
 * 消息弹窗仍是暗色。这里用 layout 渲染时提供的真实 setInitialState 对齐。
 *
 * 注意：getInitialState() 是异步的，解析完成后会整体覆盖 initialState；
 * 若首次同步发生在它解析之前，就会被冲掉。因此这里以“当前 initialState 的
 * 实际值”作为依赖与判断依据，被覆盖后会再次对齐，而不是只同步一次。
 */
const LayoutSettingsSync: React.FC<Props> = ({
  currentNavTheme,
  currentTitle,
  currentUser: initialStateUser,
  setInitialState,
}) => {
  const { navTheme } = useTheme();
  const { currentUser } = useSession();
  const [locale, setLocale] = React.useState(getStoredBrandLocale);
  const userSyncKey = React.useMemo(
    () => getUserSyncKey(currentUser),
    [currentUser],
  );

  React.useEffect(() => {
    const syncLocale = () => setLocale(getStoredBrandLocale());
    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);

    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  React.useEffect(() => {
    if (typeof setInitialState !== 'function') {
      return;
    }

    const title = syncBrandDocument(locale);
    if (
      currentNavTheme === navTheme &&
      currentTitle === title &&
      getUserSyncKey(initialStateUser) === userSyncKey
    ) {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;
      const prevSettings = prev.settings || {};
      if (
        prevSettings.navTheme === navTheme &&
        prevSettings.title === title &&
        getUserSyncKey(prev.currentUser) === userSyncKey
      ) {
        return prev;
      }

      return {
        ...prev,
        currentUser,
        settings: {
          ...prevSettings,
          navTheme,
          title,
        },
      };
    });
  }, [
    currentNavTheme,
    currentTitle,
    currentUser,
    initialStateUser,
    locale,
    navTheme,
    setInitialState,
    userSyncKey,
  ]);

  return null;
};

export default LayoutSettingsSync;
